import { Button } from "primereact/button";

import { Dialog, StateMessage } from "@/ds";
import { PapeisObrigatorios } from "@/shared/components/papeis-obrigatorios";
import { TagSimulacao } from "../simulacao/tag-simulacao";
import { EquipeAviso } from "@/shared/components/equipe-obra/equipe-aviso";
import { EquipeVazia } from "@/shared/components/equipe-obra/equipe-vazia";
import { AdicionarPapel } from "@/shared/components/equipe-obra/adicionar-papel";
import { CalendarioAlocacaoTab } from "@/shared/components/equipe-obra/calendario-alocacao-tab";
import { ConflitosAlocacaoDialog } from "@/shared/components/equipe-obra/conflitos-alocacao-dialog";
import { EquipeIncompletaDialog } from "@/shared/components/equipe-obra/equipe-incompleta-dialog";
import { EquipeTabela } from "@/shared/components/equipe-obra/equipe-tabela";
import { ProfissionalIndisponivelDialog } from "@/shared/components/equipe-obra/profissional-indisponivel-dialog";
import {
  EQUIPE_CARD_FIM,
  EQUIPE_CARD_INICIO,
  EQUIPE_CARD_OBRA,
  EQUIPE_CALENDARIO_LOADING_LABEL,
  EQUIPE_GRAVADA_ERROR_TITLE,
  EQUIPE_GRAVADA_FORBIDDEN_TITLE,
  EQUIPE_GRAVADA_LOADING,
  EQUIPE_GRAVADA_RETRY_LABEL,
  EQUIPE_HEADLINE,
} from "./definir-equipe-obra.constants";
import {
  EQUIPE_SECTION_TITLE,
  EQUIPE_TABS_LABEL,
} from "@/shared/components/equipe-obra/equipe-obra.constants";
import styles from "./definir-equipe-obra.module.scss";
import type {
  DefinirEquipeObraDialogProps,
  DefinirEquipeObraDialogViewModel,
} from "./types-definir-equipe-obra";

/**
 * Modal "Definir Equipe de Obra": abas "Papéis e Profissionais" (resumo da obra, papéis obrigatórios e a
 * tabela da equipe) e "Calendário de Alocação"; os modais do Salvar (conflitos, indisponibilidade e equipe
 * incompleta) abrem por cima.
 */
export function DefinirEquipeObraDialogView(
  props: DefinirEquipeObraDialogProps & DefinirEquipeObraDialogViewModel,
) {
  const {
    obra,
    equipeDefinida = false,
    titulo,
    modoSimulacao,
    fechar,
    aba,
    abas,
    abaAtivaDomId,
    selecionarAba,
    today,
    resumoObra,
    obraRotulo,
    obrigatorios,
    adicionados,
    obrigatoriosAbertos,
    alternarObrigatorios,
    carregandoOpcoes,
    papeisOpcoes,
    papeis,
    profissionais,
    profissionaisCalendario,
    profissionaisError,
    linhas,
    novas,
    busy,
    formError,
    estadoEquipe,
    equipeErro,
    recarregarEquipe,
    vazioMensagem,
    adicionarPapel,
    alterarLinha,
    confirmarLinha,
    cancelarLinha,
    editarLinha,
    removerLinha,
    salvarLabel,
    salvarCarregando,
    salvarBloqueado,
    confirmar,
    etapa,
    maxJustificativa,
    voltarVerificacao,
    confirmarConflitos,
    confirmarIndisponivel,
    confirmarIncompleta,
  } = props;

  return (
    <>
      <Dialog
        visible
        size="xl"
        className={styles.dialog}
        contentClassName={styles.content}
        header={
          <>
            {titulo}
            {modoSimulacao ? <TagSimulacao /> : null}
          </>
        }
        footer={
          <div className={styles.footer}>
            <Button
              type="button"
              label="Cancelar"
              severity="secondary"
              text
              onClick={fechar}
              disabled={busy}
              data-testid="equipe-obra-cancel"
            />
            <Button
              type="button"
              label={salvarLabel}
              loading={salvarCarregando}
              disabled={salvarBloqueado}
              onClick={confirmar}
              data-testid="equipe-obra-save"
            />
          </div>
        }
        // O foco inicial não deve cair no primeiro botão do rodapé.
        focusOnShow={false}
        ariaCloseIconLabel="Fechar"
        onHide={fechar}
        data-testid="equipe-obra-dialog"
      >
        <div className={styles.tabs} role="tablist" aria-label={EQUIPE_TABS_LABEL}>
          {abas.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={item.domId}
              className={`${styles.tab} ${item.ativa ? styles.tabActive : ""}`}
              aria-selected={item.ativa}
              aria-controls="equipe-obra-panel"
              onClick={() => selecionarAba(item.id)}
              data-testid={item.domId}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div
          className={styles.panel}
          role="tabpanel"
          id="equipe-obra-panel"
          aria-labelledby={abaAtivaDomId}
        >
          {aba === "calendario" ? (
            <CalendarioAlocacaoTab
              obra={obra}
              novas={novas}
              profissionais={profissionaisCalendario}
              today={today}
            />
          ) : (
            <>
              <h3 className={styles.headline}>{EQUIPE_HEADLINE}</h3>

              <div className={styles.cards}>
                <div className={styles.card} data-testid="equipe-obra-card-obra">
                  <span className={styles.cardLabel}>{EQUIPE_CARD_OBRA}</span>
                  <span className={styles.cardValue}>{resumoObra.nome}</span>
                </div>
                <div className={styles.card} data-testid="equipe-obra-card-inicio">
                  <span className={styles.cardLabel}>{EQUIPE_CARD_INICIO}</span>
                  <span className={styles.cardValue}>{resumoObra.inicio}</span>
                </div>
                <div className={styles.card} data-testid="equipe-obra-card-fim">
                  <span className={styles.cardLabel}>{EQUIPE_CARD_FIM}</span>
                  <span className={styles.cardValue}>{resumoObra.fim}</span>
                </div>
              </div>

              <PapeisObrigatorios
                papeis={obrigatorios}
                adicionados={adicionados}
                periodo={{ inicio: obra.inicio, fim: obra.fim }}
                hoje={today}
                carregando={carregandoOpcoes}
                aberto={obrigatoriosAbertos}
                onAlternar={alternarObrigatorios}
                onAdicionar={adicionarPapel}
                somenteLeitura={equipeDefinida}
                textoCarregando={EQUIPE_CALENDARIO_LOADING_LABEL}
              />

              <section className={styles.team} data-testid="equipe-obra-team">
                <div className={styles.teamHeader}>
                  <h4 className={styles.sectionTitle}>{EQUIPE_SECTION_TITLE}</h4>
                  {equipeDefinida ? null : (
                    <AdicionarPapel
                      papeis={papeis}
                      periodo={{ inicio: obra.inicio, fim: obra.fim }}
                      temLinhas={linhas.length > 0}
                      disabled={carregandoOpcoes || busy}
                      onAdicionar={adicionarPapel}
                    />
                  )}
                </div>

                {formError ? (
                  <div role="alert" data-testid="equipe-obra-form-error">
                    <EquipeAviso severity="error" text={formError} />
                  </div>
                ) : null}

                {estadoEquipe === "forbidden" ? (
                  <StateMessage
                    variant="forbidden"
                    title={EQUIPE_GRAVADA_FORBIDDEN_TITLE}
                    testId="equipe-obra-forbidden"
                  />
                ) : null}
                {estadoEquipe === "error" ? (
                  <StateMessage
                    variant="error"
                    title={EQUIPE_GRAVADA_ERROR_TITLE}
                    description={equipeErro ?? undefined}
                    testId="equipe-obra-error"
                    action={{
                      label: EQUIPE_GRAVADA_RETRY_LABEL,
                      icon: "refresh",
                      onClick: () => recarregarEquipe?.(),
                      testId: "equipe-obra-retry",
                    }}
                  />
                ) : null}
                {estadoEquipe === "loading" ? (
                  <StateMessage
                    variant="loading"
                    title={EQUIPE_GRAVADA_LOADING}
                    testId="equipe-obra-gravada-loading"
                  />
                ) : null}

                {estadoEquipe === null && linhas.length === 0 ? (
                  <EquipeVazia mensagem={vazioMensagem} />
                ) : null}

                {estadoEquipe === null && linhas.length > 0 ? (
                  <EquipeTabela
                    linhas={linhas}
                    papeis={papeisOpcoes}
                    profissionais={profissionais}
                    profissionaisError={profissionaisError}
                    disabled={busy}
                    onChange={alterarLinha}
                    onConfirm={confirmarLinha}
                    onCancel={cancelarLinha}
                    onEdit={editarLinha}
                    onRemove={removerLinha}
                  />
                ) : null}
              </section>
            </>
          )}
        </div>
      </Dialog>

      {etapa?.tipo === "conflitos" ? (
        <ConflitosAlocacaoDialog
          obra={obra}
          conflitos={etapa.conflitos}
          mostrarTrecho
          maxJustificativa={maxJustificativa}
          onVoltar={voltarVerificacao}
          onConfirmar={confirmarConflitos}
        />
      ) : null}

      {etapa?.tipo === "indisponivel" ? (
        <ProfissionalIndisponivelDialog
          itens={etapa.itens}
          maxJustificativa={maxJustificativa}
          onVoltar={voltarVerificacao}
          onConfirmar={confirmarIndisponivel}
        />
      ) : null}

      <EquipeIncompletaDialog
        itens={etapa?.tipo === "incompleta" ? etapa.itens : null}
        obraRotulo={obraRotulo}
        onHide={voltarVerificacao}
        onConfirm={confirmarIncompleta}
        simulacao={modoSimulacao}
      />
    </>
  );
}
